import React, { useState } from "react";
import {
  FaUser,
  FaIdCard,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaLock,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";

export default function CustomerLogin() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState({
    name: "",
    adhar_card: "",
    address: "",
    phone_number: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [passwordFeedback, setPasswordFeedback] = useState([]);
  const [isPasswordValid, setIsPasswordValid] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "password") {
      const feedback = validatePassword(value);
      setPasswordFeedback(feedback);
      setIsPasswordValid(feedback.length === 0);
    }
  };

  const nextStep = () => setStep((prev) => prev + 1);
  const prevStep = () => setStep((prev) => prev - 1);
  const BASE_URL = process.env.REACT_APP_SERVER_BASE_URL;
  const validatePassword = (password) => {
    const feedback = [];
    if (password.length < 8) feedback.push("At least 8 characters");
    if (!/[A-Z]/.test(password)) feedback.push("At least one uppercase letter");
    if (!/[0-9]/.test(password)) feedback.push("At least one number");
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password))
      feedback.push("At least one special character");
    return feedback;
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${BASE_URL}/users/customer-signup.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
        credentials: "include", // Important for cookies/session
      });

      const data = await response.json();

      if (response.ok) {
        alert("Signed up successfully! Customer ID: " + data.customer_id);
        window.location.href = "/login";
      } else {
        alert("Error: " + data.error);
      }
    } catch (error) {
      alert("Something went wrong. Try again later.");
      console.error("Signup error:", error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${BASE_URL}/users/customer-login.php`, {
        method: "POST",
        credentials: "include", // Important: to send and receive cookies (session)
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Show error from backend
        alert(data.error || "Login failed");
        return;
      }

      if (data.success) {
        alert("Login successful!");
        window.location.href = "/";
      } else {
        alert("Login failed");
      }
    } catch (error) {
      alert("Network error: " + error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-300 via-gray-200 to-indigo-100 relative flex items-center justify-center px-4 sm:px-6 overflow-hidden">
      {/* Abstract soft circles for subtle decor */}
      <div className="absolute top-[-100px] left-[-100px] w-72 h-72 bg-orange-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
      <div className="absolute bottom-[-120px] right-[-120px] w-80 h-80 bg-orange-300 rounded-full mix-blend-multiply filter blur-2xl opacity-30 animate-pulse"></div>

      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-6xl flex flex-col md:flex-row overflow-hidden">
        {/* Left Panel */}
        <div className="w-full md:w-1/2 bg-gray-200 p-8 sm:p-12 flex flex-col justify-center items-start">
          <div className="mb-8 sm:mb-10 flex items-start space-x-3">
            <img
              src="../assets/logo.png"
              alt="logo"
              className="w-20 h-20 sm:w-24 sm:h-24"
            />
            <div className="mt-3 sm:mt-4">
              <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-700 tracking-wide">
                Baba Fakruddin
              </h1>
              <p className="text-base sm:text-lg text-gray-500 mt-1">
                Room Booking
              </p>
            </div>
          </div>

          <h2 className="text-2xl sm:text-4xl font-bold mb-4 sm:mb-6 leading-tight">
            {isSignUp ? "Join Us & Book Your Room" : "Welcome Back"}
          </h2>
          <p className="mb-6 sm:mb-10 text-gray-500 max-w-xs text-sm sm:text-base">
            {isSignUp
              ? "Fill in your details to create an account and start booking rooms easily."
              : "Login to manage your bookings and enjoy exclusive offers."}
          </p>

          <button
            onClick={() => {
              setIsSignUp(!isSignUp);
              setStep(1);
            }}
            className="py-2 px-6 sm:py-3 sm:px-8 rounded-full border-2 border-orange-300 text-orange-400 font-semibold hover:bg-white hover:text-orange-700 transition text-sm sm:text-base"
          >
            {isSignUp ? "Back to Login" : "Create Account"}
          </button>
        </div>

        {/* Right Panel (Forms) */}
        <div className="w-full md:w-1/2 p-8 sm:p-12 flex flex-col justify-center">
          {isSignUp ? (
            <form
              onSubmit={handleSignUp}
              className="space-y-6 sm:space-y-8 text-orange-600"
            >
              <h3 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6 border-b border-orange-300 pb-2">
                {step === 1 && "Step 1: Personal Info"}
                {step === 2 && "Step 2: Contact Info"}
                {step === 3 && "Step 3: Account Setup"}
              </h3>

              {step === 1 && (
                <>
                  <InputField
                    icon={<FaUser className="text-orange-600" />}
                    type="text"
                    name="name"
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={handleChange}
                    description="Name should be as per Aadhaar card"
                  />
                  <InputField
                    icon={<FaIdCard className="text-orange-600" />}
                    type="text"
                    name="adhar_card"
                    placeholder="Aadhar Card Number"
                    value={formData.adhar_card}
                    onChange={handleChange}
                  />
                </>
              )}

              {step === 2 && (
                <>
                  <InputField
                    icon={<FaMapMarkerAlt className="text-orange-500" />}
                    type="text"
                    name="address"
                    placeholder="Address"
                    value={formData.address}
                    onChange={handleChange}
                  />
                  <InputField
                    icon={<FaPhone className="text-orange-600" />}
                    type="text"
                    name="phone_number"
                    placeholder="Phone Number"
                    value={formData.phone_number}
                    onChange={handleChange}
                  />
                </>
              )}

              {step === 3 && (
                <>
                  <InputField
                    icon={<FaEnvelope className="text-orange-600" />}
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                  <PasswordInput
                    icon={<FaLock className="text-orange-600" />}
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    show={showPassword}
                    toggleShow={() => setShowPassword((v) => !v)}
                  />
                  {passwordFeedback.length > 0 && (
                    <ul className="mt-2 text-sm text-red-600 list-disc list-inside">
                      {passwordFeedback.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  )}

                  <PasswordInput
                    icon={<FaLock className="text-orange-600" />}
                    name="confirmPassword"
                    placeholder="Confirm Password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    show={showConfirmPassword}
                    toggleShow={() => setShowConfirmPassword((v) => !v)}
                  />
                </>
              )}

              <div className="flex justify-between">
                {step > 1 && (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="px-5 py-2 rounded bg-orange-100 text-orange-700 hover:bg-orange-200 transition"
                  >
                    Back
                  </button>
                )}
                {step < 3 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="px-5 py-2 rounded bg-orange-400 text-white hover:bg-orange-500 transition"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="px-5 py-2 rounded bg-orange-600 text-white hover:bg-orange-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={
                      !isPasswordValid ||
                      formData.password !== formData.confirmPassword ||
                      !formData.password
                    }
                  >
                    Sign Up
                  </button>
                )}
              </div>
            </form>
          ) : (
            <form
              onSubmit={handleLogin}
              className="space-y-6 sm:space-y-8 text-orange-900"
            >
              <h3 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6 border-b border-orange-300 pb-2">
                Login
              </h3>
              <InputField
                icon={<FaEnvelope className="text-orange-600" />}
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
              />
              <PasswordInput
                icon={<FaLock className="text-orange-600" />}
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                show={showLoginPassword}
                toggleShow={() => setShowLoginPassword((v) => !v)}
              />
              <button className="w-full py-3 rounded bg-orange-400 text-white hover:bg-orange-500 transition">
                Login
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function PasswordInput({
  icon,
  name,
  placeholder,
  value,
  onChange,
  show,
  toggleShow,
}) {
  return (
    <div className="flex items-center border border-orange-300 rounded-md px-4 py-3 focus-within:ring-2 focus-within:ring-orange-400 transition relative">
      <div className="mr-3 text-xl">{icon}</div>
      <input
        type={show ? "text" : "password"}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required
        className="w-full outline-none text-black placeholder-gray-500 bg-transparent"
      />
      <div
        className="absolute right-4 cursor-pointer text-orange-600"
        onClick={toggleShow}
        aria-label={show ? "Hide password" : "Show password"}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") toggleShow();
        }}
      >
        {show ? <FaEyeSlash /> : <FaEye />}
      </div>
    </div>
  );
}

function InputField({
  icon,
  type,
  name,
  placeholder,
  value,
  onChange,
  description,
}) {
  return (
    <div className="flex flex-col">
      <div className="flex items-center border border-orange-300 rounded-md px-4 py-3 focus-within:ring-2 focus-within:ring-orange-400 transition">
        <div className="mr-3 text-xl">{icon}</div>
        <input
          type={type}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          required
          className="w-full outline-none text-black placeholder-gray-500 bg-transparent"
        />
      </div>
      {description && (
        <p className="mt-1 text-sm text-orange-600  select-none">
          {description}
        </p>
      )}
    </div>
  );
}
